import { useEffect, useState, useCallback } from "react";
import Card from "components/card";
import LineChart from "components/charts/LineChart";
import {
  MdArrowDropUp,
  MdArrowDropDown,
  MdBarChart,
  MdOutlineCalendarToday,
} from "react-icons/md";
import { lineChartOptionsTotalSpent } from "utils/charts";
import { adminApiService } from "../../../services/api";

const TotalSpent = () => {
  const [period, setPeriod] = useState("monthly");
  const [totalSales, setTotalSales] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartOptions, setChartOptions] = useState(lineChartOptionsTotalSpent);

  // Fetch data from API based on selected period
  const fetchTotalSalesByPeriod = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApiService.getTotalSales(period);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
      setTotalSales(parseFloat(result.current_period.total_amount || 0));
      setTotalOrders(result.current_period.number_of_orders || 0);
    } catch (error) {
      console.error("Failed to fetch total sales:", error);
      setData(null);
      setTotalSales(0);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchTotalSalesByPeriod();
  }, [fetchTotalSalesByPeriod]);

  // Update chart options with dynamic x-axis and dual y-axis
  useEffect(() => {
    if (data?.current_period?.time_series?.labels) {
      setChartOptions((prev) => ({
        ...prev,
        xaxis: {
          ...prev.xaxis,
          categories: data.current_period.time_series.labels,
          labels: {
            style: {
              colors: "#A3AED0",
              fontSize: "12px",
              fontWeight: "500",
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: [
          {
            title: { text: "Sales (TK)" },
            labels: {
              formatter: (val) => val.toLocaleString(),
            },
            min: 0,
          },
          {
            opposite: true,
            title: { text: "Orders" },
            min: 0,
          },
        ],
      }));
    }
  }, [data]);

  // Set chart series with sales and orders data and totals
  useEffect(() => {
    if (data?.current_period?.time_series) {
      setChartSeries([
        {
          name: "Sales",
          data: data.current_period.time_series.sales,
          color: "#4318FF",
          yAxisIndex: 0,
        },
        {
          name: "Orders",
          data: data.current_period.time_series.orders,
          color: "#6AD2FF",
          yAxisIndex: 1,
        },
      ]);
      setTotalSales(parseFloat(data.current_period.total_amount || 0));
      setTotalOrders(data.current_period.number_of_orders || 0);
    } else {
      // Clear chart if no data
      setChartSeries([]);
      setTotalSales(0);
      setTotalOrders(0);
    }
  }, [data]);

  const overviewText = `${
    period.charAt(0).toUpperCase() + period.slice(1)
  } overview`;

  if (loading) {
    return (
      <Card extra="!p-[20px] text-center">
        <div className="flex h-64 items-center justify-center">
          <div className="flex animate-pulse flex-col items-center">
            <div className="mb-2 h-4 w-32 rounded bg-gray-200"></div>
            <div className="h-40 w-full rounded bg-gray-100"></div>
          </div>
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card extra="!p-[20px] text-center">
        <div className="flex h-64 flex-col items-center justify-center">
          <div className="mb-4 rounded-full bg-red-100 p-3">
            <svg
              className="h-6 w-6 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-700">
            Failed to load data
          </p>
          <p className="text-sm text-gray-500">
            Please try again or check your connection
          </p>
          <button
            onClick={fetchTotalSalesByPeriod}
            className="hover:bg-primary-dark mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  const trendPositive = data.trend?.isPositive;
  const trendNegative = data.trend?.isNegative;
  const trendDirection = data.trend?.direction || "neutral";
  const trendValue = parseFloat(data.trend?.value || "0").toFixed(2);

  const trendClasses = trendPositive
    ? "bg-green-50 text-green-600"
    : trendNegative
    ? "bg-red-50 text-red-600"
    : "bg-gray-50 text-gray-600";

  return (
    <Card extra="!p-6">
      <div className="flex flex-col dark:text-white">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <MdBarChart className="text-2xl text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Total Sales & Orders
              </h2>
              <p className="text-sm text-gray-500">{overviewText}</p>
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MdOutlineCalendarToday className="text-primary" />
            </div>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="block w-full appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-8 text-sm font-medium text-gray-700 shadow-sm hover:border-gray-300 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="today">Today</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Stats and Chart */}
        <div className="mt-8 flex flex-col gap-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-4xl font-bold text-gray-900 dark:text-white">
                {totalSales.toLocaleString()} TK
              </p>
              <p className="mt-1 text-lg font-medium text-gray-600">
                {totalOrders} Orders
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div
                className={`flex items-center rounded-lg px-3 py-1.5 ${trendClasses}`}
              >
                {trendPositive ? (
                  <MdArrowDropUp className="text-xl text-green-600" />
                ) : trendNegative ? (
                  <MdArrowDropDown className="text-xl text-red-600" />
                ) : (
                  <span className="text-sm font-medium">{trendDirection}</span>
                )}
                <span className="ml-1 text-sm font-medium">{trendValue}%</span>
              </div>
              <span className="text-sm text-gray-500">vs last period</span>
            </div>
          </div>

          <div className="h-64 w-full">
            <LineChart
              options={chartOptions}
              series={chartSeries}
              height="100%"
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default TotalSpent;
